import { Component, OnInit, EventEmitter, Input } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  UntypedFormArray,
} from '@angular/forms';
import { MobileLengthPerCode } from '@shared-features/enums/mobile-length-per-code.enum';
import { Receiver } from '../../../receivers/models/receiver';
import { BaseComponent } from '@shared/components/base-component/base.component';
import {
  EmailValidator,
  MobileNumberLengthValidator,
  PhoneNumberTypeValidator,
  PhoneNumberValidator,
} from '@shared/validators/form-validators';
import { ConfigConstant } from '@shared/constants/config.constant';
import { LocationType } from '@shared/enums/location-type.enum';
import { ReceiverType } from '@shared-features/enums/receiver-type.enum';
import { ActivatedRoute } from '@angular/router';
import { FunctionUtil } from '@shared/utils/function.util';
import { LayoutService } from '@shared/services/layout.service';
import { RolesConstants } from '@shared/model/roles-constants';
import { LocationModel } from '@features/live-tracking/models/location.model';
import { Country } from '@shared-features/models/country';
import { ReceiversCommonApiService } from '@shared-features/services/receivers-common-api';
import { ReceiversApiService } from '@features/receivers/services/receivers-api.service';
import { SharedApiService } from '@shared-features/services/shared-api.service';

@Component({
  selector: 'app-view-edit-receiver',
  templateUrl: './view-edit-receiver.component.html',
  styleUrls: ['./view-edit-receiver.component.scss'],
})
export class ViewEditReceiverComponent extends BaseComponent implements OnInit {
  @Input() readOnly = false;
  receiverId: number;
  customerId: number;
  receiverType: ReceiverType;

  types = ReceiverType;
  form: UntypedFormGroup;
  contacts: UntypedFormArray;
  countries: Country[] = [];
  selectedReceiver: Receiver = new Receiver();
  LocationType = LocationType;
  locationIds: number[] = [];
  errorHappened: boolean = false;
  viewMode: boolean = true;

  generalReceiver: boolean = false;
  RolesConstants = RolesConstants;
  onSelectLocation = new EventEmitter();
  selectedMapLocation: LocationModel;
  MobileLengthPerCode = MobileLengthPerCode;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private activatedRoute: ActivatedRoute,
    public layoutService: LayoutService,
    private sharedApiService: SharedApiService,
    private receiversApiService: ReceiversApiService,
    private receiversCommonApiService: ReceiversCommonApiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.receiverType = this.activatedRoute.snapshot.data?.receiverType;
    const customerId = this.activatedRoute.snapshot.params?.customerId;
    const receiverId = this.activatedRoute.snapshot.params?.receiverId;
    if (this.receiverType === ReceiverType.General) this.generalReceiver = true;
    if (receiverId) {
      this.receiverId = parseInt(receiverId);
      this.getReceiverById();
    }
    if (customerId) this.customerId = parseInt(customerId);
  }

  private getReceiverById(): void {
    this.load(this.receiversCommonApiService.getReceiverById(this.receiverId), {
      isLoadingTransparent: true,
      fetchOnce: true,
    }).subscribe((data) => (this.selectedReceiver = data));
  }

  onEditButtonClicked() {
    this.load(this.sharedApiService.getAllCountries(), {
      isLoadingTransparent: true,
      fetchOnce: true,
    }).subscribe((data) => {
      this.countries = data;
      this.initForm();
      this.setReceiverExistingLocationIds();
      this.viewMode = false;
    });
  }

  onAddLocationId(event) {
    this.locationIds.push(event);
  }

  onCancelButtonClicked() {
    this.viewMode = true;
    this.form.reset();
  }

  onSaveButtonClicked() {
    let editedReceiver = new Receiver();

    if (!this.errorHappened) {
      editedReceiver.id = this.selectedReceiver.id;
      editedReceiver.name = this.form.value['companyName'];
      this.form.value['contacts'].forEach((contact, index) => {
        let objOfExistingValues = {
          dropDownDisplayName: null,
          dropDownValue: null,
        };
        if (contact.id) objOfExistingValues['id'] = contact.id;
        if (contact.firstName) objOfExistingValues['firstName'] = contact.firstName;
        if (contact.lastName) objOfExistingValues['lastName'] = contact.lastName;
        if (contact.email) objOfExistingValues['email'] = contact.email;
        if (contact.mobile)
          objOfExistingValues['mobileNumber'] =
            contact.countryCode.toString() +
            ConfigConstant.MOBILE_NUMBER_SEPARATOR +
            contact.mobile.toString();
        if (contact.isDefault) objOfExistingValues['isDefault'] = contact.isDefault;

        editedReceiver.contactPerson.push(objOfExistingValues);
      });
    }

    this.errorHappened = false;

    this.load(
      this.receiversApiService.updateReceiver(
        editedReceiver,
        this.receiverType,
        this.locationIds,
        this.customerId || this.selectedReceiver.customer?.id
      ),
      { isLoadingTransparent: true, fetchOnce: true }
    ).subscribe((data) => {
      this.selectedReceiver = data;
      if (!this.viewMode) {
        this.asyncFeedbackService.showSuccessMessage(
          this.translationsList['SuccessMessages']['ReceiverUpdateSuccessfully']
        );
        this.viewMode = true;
      }
    });
  }

  initForm() {
    this.form = this.formBuilder.group({
      companyName: new UntypedFormControl(this.selectedReceiver.name),
      contacts: this.getReceiverExistingContacts(),
    });
  }

  getReceiverExistingContacts() {
    let _contacts: any = [];
    for (let i = 0; i < this.selectedReceiver.contactPerson.length; i++) {
      let _contact = this.selectedReceiver.contactPerson[i];

      _contacts.push(
        this.formBuilder.group({
          firstName: new UntypedFormControl(_contact.firstName, Validators.required),
          lastName: new UntypedFormControl(_contact.lastName, Validators.required),
          email: new UntypedFormControl(_contact.email, [EmailValidator]),
          countryCode: new UntypedFormControl(
            _contact.mobileNumber?.includes('-')
              ? _contact.mobileNumber?.split(ConfigConstant.MOBILE_NUMBER_SEPARATOR)[0]
              : FunctionUtil.getSaudiArabiaPhoneKey(this.countries)
          ),
          mobile: new UntypedFormControl(
            _contact.mobileNumber?.split(ConfigConstant.MOBILE_NUMBER_SEPARATOR)[1]
          ),
          id: new UntypedFormControl(_contact.id),
          isDefault: new UntypedFormControl(_contact.isDefault),
        })
      );
    }

    return this.formBuilder.array([..._contacts]) as UntypedFormArray;
  }

  createContact(): UntypedFormGroup {
    const group = this.formBuilder.group({
      firstName: new UntypedFormControl('', [Validators.required]),
      lastName: new UntypedFormControl('', [Validators.required]),
      email: new UntypedFormControl('', [EmailValidator]),
      mobile: new UntypedFormControl(null, [
        PhoneNumberValidator,
        PhoneNumberTypeValidator,
        MobileNumberLengthValidator(() => this.form?.value?.countryCode),
      ]),
      countryCode: new UntypedFormControl(FunctionUtil.getSaudiArabiaPhoneKey(this.countries), [
        Validators.required,
      ]),
      id: new UntypedFormControl(null),
      isDefault: new UntypedFormControl(null),
    });
    group.controls.mobile.setValidators([
      PhoneNumberValidator,
      PhoneNumberTypeValidator,
      MobileNumberLengthValidator(() => group?.controls.countryCode.value || ''),
    ]);
    return group;
  }

  addContact(): void {
    this.contacts = this.form.get('contacts') as UntypedFormArray;
    this.contacts.push(this.createContact());
  }

  setReceiverExistingLocationIds() {
    this.selectedReceiver.locations.forEach((location: LocationModel) => {
      this.locationIds.push(location.id);
    });
  }

  onNavigateToMap(event) {
    this.onSelectLocation.emit(event);
    this.selectedMapLocation = event;
    this.scrollTolocationsCard();
  }

  scrollTolocationsCard() {
    document.getElementById('locationsCard').scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }
}
